import { EVENT_KINDS, IdSchema, type EventKind } from '@batiflow/contracts';
import { z } from 'zod';

export const CALENDAR_VIEWS = ['week', 'workWeek', 'month', 'agenda'] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day written `YYYY-MM-DD` (2026-02-30 is not one). */
export function isDay(value: unknown): value is string {
  if (typeof value !== 'string' || !DAY_RE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value);
}

export const isKind = (value: unknown): value is EventKind =>
  typeof value === 'string' && (EVENT_KINDS as readonly string[]).includes(value);

/**
 * Search params of /o/$orgId/calendar. They are the state the list pane (mini-month, filters) and
 * the main pane share, so a link keeps the view: `view` (week, work week, month, agenda), `date`
 * (the day the view is built around), `event` (opens its details, from a reminder notification),
 * `scope` (`all`: every organisation of the person), `hide` (kinds left out, comma separated)
 * and `project`. An invalid value is simply dropped.
 */
export const CalendarSearchSchema = z.object({
  view: z.enum(CALENDAR_VIEWS).optional().catch(undefined),
  date: z.string().refine(isDay).optional().catch(undefined),
  event: IdSchema.optional().catch(undefined),
  scope: z.enum(['org', 'all']).optional().catch(undefined),
  hide: z.string().max(100).optional().catch(undefined),
  project: IdSchema.optional().catch(undefined),
  /** `meetings`: the organisation's meetings instead of the calendar grid (COMX-FE-03). */
  section: z.literal('meetings').optional().catch(undefined),
});
export type CalendarSearch = z.infer<typeof CalendarSearchSchema>;

/** The kinds named by `hide` (unknown names are ignored). */
export function hiddenKindsOf(hide: string | undefined): EventKind[] {
  return (hide ?? '').split(',').filter(isKind);
}
